import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Pivots one coverage payload into whichever grain is being read.
 *
 * The API returns a row per company, provider and utility type. Every view here is a fold of that
 * one payload rather than its own endpoint, so the totals can never disagree between tabs.
 */
export const GRAINS = {
  company: {
    label: 'Customer',
    key: r => r.companyId || 'unknown',
    name: r => r.companyName || 'Unknown company'
  },
  provider: {
    label: 'Provider',
    key: r => r.utilityProviderId || r.providerName || 'unknown',
    name: r => r.providerName || 'Unknown provider'
  },
  // Never undefined: the grid keys its rows on this, and an undefined id takes the whole tab down.
  utilityType: {
    label: 'Utility',
    key: r => r.utilityTypeName || 'unknown',
    name: r => r.utilityTypeName || 'Unknown utility'
  }
};

/**
 * Manual is what is left after automation, not a column of its own: a utility is either proven
 * end to end or somebody is still fetching it. Wired sits between them and is a subset of manual,
 * so adding the three would double count.
 */
export const pivotCoverage = function () {
  let rows = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  let grain = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'company';
  const spec = GRAINS[grain] || GRAINS.company;
  const buckets = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const key = spec.key(row);
    const bucket = buckets.get(key) || {
      key,
      name: spec.name(row),
      utilities: 0,
      automated: 0,
      wired: 0,
      notProven: 0
    };
    const wired = row.wiredUtilities || 0;
    const automated = row.automatedUtilities || 0;
    bucket.utilities += row.utilities || 0;
    bucket.automated += automated;
    bucket.wired += wired;
    // Clamped per source row, not on the fold. Automation is credited even where the registry has
    // no current fetcher, so a row at 0 wired / 10 automated would otherwise cancel out a sibling
    // row at 10 wired / 0 automated and report nothing left to prove.
    bucket.notProven += Math.max(0, wired - automated);
    buckets.set(key, bucket);
  }
  return [...buckets.values()]
  // percent is materialised, not computed in a renderCell: the grid sorts and filters on the
  // row value, so a column that only exists at render time orders by undefined.
  .map(b => _objectSpread(_objectSpread({}, b), {}, {
    manual: Math.max(0, b.utilities - b.automated),
    percent: b.utilities ? Math.round(b.automated / b.utilities * 100) : 0
  }))
  // Most manual first: the top row is always the next thing worth automating.
  .sort((a, b) => b.manual - a.manual || b.utilities - a.utilities);
};

/** Share of utilities proven automated end to end. Weighted by utilities, never by row count. */
export const automatedShare = function () {
  let rows = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  const totals = (Array.isArray(rows) ? rows : []).reduce((acc, r) => ({
    utilities: acc.utilities + (r.utilities || 0),
    automated: acc.automated + (r.automatedUtilities || 0)
  }), {
    utilities: 0,
    automated: 0
  });
  return _objectSpread(_objectSpread({}, totals), {}, {
    percent: totals.utilities ? Math.round(totals.automated / totals.utilities * 100) : 0
  });
};

/**
 * The ingest funnel with its drops named.
 *
 * Each stage carries what it lost from the one before, because the gap is the finding: fetching is
 * solved and mapping is not, and a column of five raw counts does not say that.
 *
 * `pending` is deliberately not a stage. A mapped stream is either posted or still queued, so
 * listing it between mapped and posted would read as a further drop when it is the same rows.
 * It is subtracted from the last stage's loss instead: a bill waiting in the queue has not failed,
 * and counting it as lost points the bottleneck at the wrong stage.
 */
export const funnelStages = function () {
  let ingest = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const queued = ingest.pending || 0;
  const stages = [{
    label: 'Streams tracked',
    value: ingest.streams || 0
  }, {
    label: 'With a bill date',
    value: ingest.dated || 0
  }, {
    label: 'Mapped to a property',
    value: ingest.mapped || 0
  }, {
    label: 'Sent to the app',
    value: ingest.posted || 0,
    queued
  }];
  const top = stages[0].value || 0;
  return stages.map((stage, i) => _objectSpread(_objectSpread({
    queued: 0
  }, stage), {}, {
    share: top ? Math.round(stage.value / top * 100) : 0,
    lost: i === 0 ? 0 : Math.max(0, stages[i - 1].value - stage.value - (stage.queued || 0))
  }));
};